import type { ThemeOptions } from '@classroom-ai/ui/theme';

import { createPaletteChannel } from 'minimal-shared/utils';

// Student-app-only theme overrides, layered on top of the shared @classroom-ai/ui
// theme via <ThemeProvider themeOverrides={...}> (apps/teacher and apps/admin pass
// no override, so they're unaffected). Warmer, rounder, larger-touch-target feel
// appropriate for 12-14 year-old students, vs. the cool green/purple admin look.

// Full ramps (not just `main`/`contrastText`) built with the same helper the base
// theme uses (packages/ui/src/theme/core/palette.ts) — the shared "soft"/"filled"
// button mixins read the *Channel fields directly, so a partial override would
// leave stale channel values mismatched against the new main color.
const primary = createPaletteChannel({
  lighter: '#FFE0CC',
  light:   '#FF9F66',
  main:    '#E8590C', // warm orange — ~3.6:1 contrast with white (passes WCAG AA for large/bold UI text)
  dark:    '#B8430C',
  darker:  '#7A2D08',
  contrastText: '#FFFFFF',
});

const secondary = createPaletteChannel({
  lighter: '#FFF3C4',
  light:   '#FFD666',
  main:    '#F2A93C', // warm amber — needs dark text for contrast, same pattern as the
  dark:    '#C9821F', // shared theme's warning color (packages/ui/.../theme-config.ts)
  darker:  '#8F5A12',
  contrastText: '#1C252E',
});

export const studentThemeOverrides: ThemeOptions = {
  shape: { borderRadius: 12 }, // up from the shared default of 8
  colorSchemes: {
    light: {
      palette: { primary, secondary },
    },
  },
  components: {
    // Bumps default touch target app-wide (Send button, Take a Break, End Session,
    // alert actions, etc.) to MUI's `large` size (~48px min height). Only set
    // `defaultProps` here, never `styleOverrides` — the shared theme's MuiButton
    // already defines styleOverrides.root.variants as a large array (contained/
    // outlined/text/soft/size/disabled), and MUI's theme merge replaces arrays
    // wholesale rather than concatenating them, so providing our own styleOverrides
    // would silently delete all of that.
    MuiButton: {
      defaultProps: { size: 'large' },
    },
    MuiIconButton: {
      defaultProps: { size: 'large' },
    },
  },
};
